<script setup>

import {computed, getCurrentInstance, ref, watch} from "vue";
import {useRoute} from "vue-router";
import { useI18n } from "vue-i18n";
import {SecondaryButton} from '@es-bill/es-bill-common-components';
import {FRONTEND_BASEURL} from "@/config/config.js";
import { useUserStore } from "@/lib/es-bill-shared-js/auth/userStore";

// Atomic Design Imports
import DropdownMenu from '@/components/molecule/DropdownMenu.vue';
import MobileMenu from '@/components/molecule/MobileMenu.vue';

const translationDevelopment = ref(import.meta.env.VITE_I18N_DEV_MODE === 'true');
const route = useRoute();
const { t, locale } = useI18n();
const isSidebarVisible = ref(false);
const userStore = useUserStore();

let routeIsViewDsoContracts = computed(() => route.name === "dsoContracts")
let routeIsViewReplaceDsoCompanies = computed(() => route.name === "replaceDsoCompanies")
let routeIsViewDsoCompanies = computed(() => route.name === "dsoCompanies")
let routeIsViewDsoCompanyForm = computed(() => route.name === "dsoCompanyForm")
let routeIsViewEdaConnectionForm = computed(() => route.name === "edaConnectionForm")
let routeIsViewEdaConnections = computed(() => route.name === "edaConnections")
let routeIsViewMeteringPoints = computed(() => route.name === "meteringPoints")
let routeIsViewMeteringPointsAnalysis = computed(() => route.name === "meteringPointsAnalysis")
let routeIsViewStatistics = computed(() => route.name === "statistics")
let routeIsViewCommunities = computed(() => route.name === "communities")
let routeIsViewCommunityForm = computed(() => route.name === "communityForm")
let routeIsViewMessenger = computed(() => route.name === "messenger")
let routeIsViewCreatePartner = computed(() => route.name === "createPartner")
let routeIsViewDashboard = computed(() => route.name === "dashboard")

let routeIsViewExportOdoo = computed(() => route.name === "ViewExportOdoo")

// notification contacts route detection
let routeIsViewNotificationContacts = computed(() => route.name === "notificationContacts");

// Add computed for Bill Runs
let routeIsViewBillRuns = computed(() => route.name === "billRuns")

const managementMenuItems = [
  {text: 'dsoContracts', path: '/dsoContracts', active: routeIsViewDsoContracts},
  {text: 'notificationContacts', path: '/notificationContacts', active: routeIsViewNotificationContacts},
  {text: 'publicCommunityInformation', path: '/communities', active: routeIsViewCommunities},
  {text: 'dsoCompanies', path: '/dsoCompanies', active: routeIsViewDsoCompanies},
  {text: 'edaConnections', path: '/edaConnections', active: routeIsViewEdaConnections},
];

const meteringPointsMenuItems = [
  {text: 'meteringPoints', path: '/meteringPoints', active: routeIsViewMeteringPoints},
  {text: 'analysis', path: '/meteringPoints/analysis', active: routeIsViewMeteringPointsAnalysis},
  {text: 'statistics', path: '/statistics', active: routeIsViewStatistics},
];

// Add Bill Runs menu items constant
const billRunsMenuItems = [
  { text: 'dataChecks', path: '/billRuns', active: routeIsViewBillRuns }
]

const exportItems = [
  {text: 'export-odoo', path: '/exports/odoo', active: routeIsViewExportOdoo},
];

const messengerMenuItems = [
  {text: 'xp-messenger.dashboard.title', path: '/messenger/dashboard', active: routeIsViewDashboard},
  {text: 'xp-messenger.partner.create', path: '/messenger/create-partner', active: routeIsViewCreatePartner},
];

const keycloak = getCurrentInstance().appContext.app.$keycloak;

// List of available locales
const locales = [
  {text: 'Deutsch', value: 'de'},
  {text: 'English', value: 'en'},
  {text: 'Italiano', value: 'it'}
];

// Computed properties for user data
const userInitials = computed(() => {
  return keycloak.tokenParsed?.given_name?.substring(0, 1) +
         keycloak.tokenParsed?.family_name?.substring(0, 1);
});

// Computed property for management menu active state
const routeIsViewManagement = computed(() => routeIsViewDsoContracts.value || routeIsViewCommunities.value || routeIsViewEdaConnectionForm.value || routeIsViewEdaConnections.value);

// Unified menu groups configuration for both desktop and mobile
const menuGroups = computed(() => [
  {
    title: 'management',
    items: managementMenuItems,
    isActive: routeIsViewManagement.value
  },
  {
    title: 'meteringPoints',
    items: meteringPointsMenuItems,
    isActive: routeIsViewMeteringPoints.value
  },
  {
    title: 'billRuns',
    items: billRunsMenuItems,
    isActive: routeIsViewBillRuns.value
  },
  {
    title: 'exports',
    items: exportItems,
    isActive: routeIsViewExportOdoo.value
  },
  {
    title: 'xp-messenger.messenger',
    items: messengerMenuItems,
    isActive: routeIsViewMessenger.value
  }
]);

function redirectProfileEdit() {
  keycloak.accountManagement({redirectUri: FRONTEND_BASEURL});
}

function handleLogout() {
  // userStore.logout();
  keycloak.logout();
}
</script>

<template>
  <v-app-bar scroll-threshold="50" :scroll-behavior="$vuetify.display.smAndDown && !isSidebarVisible ? 'hide' : ''" height="80" v-bind="$attrs">
    <template v-slot:prepend>
      <div class="justify-start headerTitle menuBand">
        <router-link to="/"
          class="routerLinkNoDecoration">
          <img height="25px" width="120px" src="@/assets/sostrom/logo.svg" alt="so-strom-Logo"/>
        </router-link>
      </div>
      <div class="justify-start headerTitle menuBand hidden-md-and-down">
        <DropdownMenu
          v-for="group in menuGroups"
          :key="group.title"
          :title="$t(group.title)"
          :menu-items="group.items"
          :is-active="group.isActive"
        />
      </div>
    </template>

    <template v-slot:append>
      <div class="headerIcons hidden-md-and-down">
       <SecondaryButton :size="translationDevelopment ? 'default' : 'large'" :text="t('contact')" @click="$router.push({name: 'Support'})" color="dark-grey-2" />

        <v-select
            v-if="translationDevelopment"
            v-model="locale"
            :items="locales"
            item-title="text"
            item-value="value"
            variant="underlined"
            dense
        ></v-select>

        <v-btn
            icon
        >
          <v-avatar color="primary" class="avatarIcon" @click="redirectProfileEdit()">
          <span class="avatarIconText">
              {{
                keycloak.tokenParsed?.given_name?.substring(0, 1) +
                keycloak.tokenParsed?.family_name?.substring(0, 1)
              }}</span>
          </v-avatar>
        </v-btn>

        <v-btn size="normal" icon="mdi-logout" color="#878787" @click="handleLogout"></v-btn>
      </div>

      <div class="hidden-lg-and-up">
        <v-app-bar-nav-icon color="black" @click="isSidebarVisible = !isSidebarVisible"></v-app-bar-nav-icon>
      </div>

    </template>
  </v-app-bar>

  <MobileMenu
    v-model:is-visible="isSidebarVisible"
    :menu-groups="menuGroups"
    :user-initials="userInitials"
    :full-name="userStore.fullName"
    :translation-development="translationDevelopment"
    :locales="locales"
    @profile-edit="redirectProfileEdit"
    @logout="handleLogout"
  />
</template>


<script>
export default {
  name: 'HeaderBar'
}
</script>


<style scoped>
.routerLinkNoDecoration {
  text-decoration: none;
  color: inherit;
}

.v-select {
  min-width: 100px;
}



.menuBand {
  padding: 12px 12px 12px 32px;

  display: flex;
  align-items: flex-start;

  vertical-align: center;
}

.headerIcons {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-right: 32px;
}

.avatarIcon {
  height: 40px;
  width: 40px;
}

.avatarIconText {
  color: white;
}


</style>
